import type { AriaRole } from 'react';
import React, { useId } from 'react';
import { HBox, VBox } from '../layout';
import type { ChatPersonImageProps } from './ChatMessageComponents/ChatPersonImage';
import { ChatPersonImage } from './ChatMessageComponents/ChatPersonImage';
import { intl, intlFmt, useZoomBreakpoint } from '../../lib';
import { Badge, Icon, ListItem } from '../basic';
import { formatChatConversationDate } from './ChatMessageComponents/chatDateFormatting';
import { withScaledEmoji } from './ChatMessageComponents/scaledEmoji';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/** Above this many unread messages, the badge shows "9+" instead of the exact count. */
const NOTIFICATION_COUNT_CAP = 9;

export type ChatConversationProps = {
  image: ChatPersonImageProps;
  header: string;
  subheader: string;
  pinned?: boolean;
  date?: Date;
  notificationCount?: number;
  selected?: boolean;
  clickFunction: () => void;
  roomId: string;
  role?: AriaRole;
  emojiData?: EmojiData;
};
export function ChatConversation({
  image,
  header,
  subheader,
  pinned = false,
  date,
  notificationCount = 0,
  selected = false,
  clickFunction,
  roomId,
  role,
  emojiData = { categories: {}, emojis: {} }
}: ChatConversationProps): React.JSX.Element {
  const classNames = ['ehs-chat-conversation'];
  if (selected) {
    classNames.push('selected');
  }
  const userStatusId = useId();
  const notificationId = 'notification-' + roomId;
  const ariaDescribed = [];
  if (image.userStatus) {
    ariaDescribed.push(userStatusId);
  }
  if (notificationCount > 0) {
    ariaDescribed.push(notificationId);
  }

  const formattedDate = date ? formatChatConversationDate(date) : '';
  const breakLine = useZoomBreakpoint(1.3);
  if (breakLine) {
    classNames.push('conversation-panel-break-line');
  }
  const displayedNotificationCount =
    notificationCount > NOTIFICATION_COUNT_CAP
      ? `${NOTIFICATION_COUNT_CAP}+`
      : String(notificationCount);

  return (
    <ListItem
      className={classNames.join(' ')}
      onClick={clickFunction}
      aria-describedby={ariaDescribed.join(' ')}
      role={role}
      aria-selected={selected}
    >
      <ChatPersonImage {...image} userStatusId={userStatusId} />
      <VBox className="conv-title">
        <div className="title-header">{header}</div>
        <div className="title-subheader">
          <div className="sr-only">{intl('common.chatmessage.preview')}</div>
          {subheader.length > 40 ? (
            <>{withScaledEmoji(subheader.substring(0, 40), emojiData)}...</>
          ) : (
            withScaledEmoji(subheader, emojiData)
          )}
        </div>
      </VBox>
      <VBox className="conv-details">
        <div className="detail-date">
          <time
            dateTime={formattedDate}
            aria-label={intlFmt('common.chatmessage.sentOn', formattedDate)}
          >
            {formattedDate}
          </time>
        </div>
        <HBox className="detail-icons">
          {pinned && (
            <Icon
              aria-label={intl('common.chatmessage.pinned')}
              iconName="pin-1"
            />
          )}
          {notificationCount > 0 && (
            <>
              <div className="sr-only" id={notificationId}>
                {intlFmt(
                  'common.chatmessage.unRead',
                  displayedNotificationCount
                )}
              </div>
              <Badge
                badgeType="info"
                role="presentation"
                label={displayedNotificationCount}
              />
            </>
          )}
        </HBox>
      </VBox>
    </ListItem>
  );
}
